import { useEffect, useMemo } from 'react';
import { useAppDispatch } from '../../useAppDispatch';
import { siteSpeedApi } from '../service';
import { skipToken } from '@reduxjs/toolkit/query';
import { useSelector } from 'react-redux';
import { FetchLoadingTimeResult } from '../types';

/**
 * Creates an object with the region as key and loading time data as value
 */
function createLoadingTimesRecord(data: (FetchLoadingTimeResult | undefined)[], regions: string[]): Record<string, FetchLoadingTimeResult> {
    return data.reduce((record, result, index) => {
        if (result) {
            return {
                ...record,
                [regions[index]]: result,
            };
        } else {
            return record;
        }
    }, {} as Record<string, FetchLoadingTimeResult>);
}

type Params = {
    url: string;
    regions: string[];
    interval: string;
    groupBy: string;
    startDate: string;
    endDate: string;
    pollingInterval?: number;
};

export default function useFetchMultipleRegionsLoadingTime({ url, regions, interval, groupBy, startDate, endDate, pollingInterval }: Params) {
    const dispatch = useAppDispatch();

    const selectLoadingTime = useMemo(
        () =>
            regions.map((region) =>
                siteSpeedApi.endpoints.fetchLoadingTime.select(regions.length > 0 ? { url, region, interval, groupBy, startDate, endDate } : skipToken)
            ),
        [endDate, groupBy, interval, regions, startDate, url]
    );

    const loadingTimeQueryStates = useSelector((state) => regions.map((_, index) => selectLoadingTime[index](state as any)));

    /**
     * The first render of a useQuery hook masks the isUninitialized flag, even if the actual dispatching of the initiate
     * util is done in an useEffect hook after the first render. This imitates the library's behavior
     */
    const isLoadingMultipleLoadingTimes = useMemo(
        () => loadingTimeQueryStates.some((query) => query.isLoading || query.isUninitialized),
        [loadingTimeQueryStates]
    );

    const groupedLoadingTimes = useMemo(
        () =>
            createLoadingTimesRecord(
                loadingTimeQueryStates.map((query) => query.data),
                regions
            ),
        [loadingTimeQueryStates, regions]
    );

    useEffect(() => {
        const subscriptions = regions.map((region) => {
            return dispatch(
                siteSpeedApi.endpoints.fetchLoadingTime.initiate(
                    { url, region, interval, groupBy, startDate, endDate },
                    { subscriptionOptions: { pollingInterval } }
                )
            );
        });

        return () => {
            subscriptions.forEach((subscription) => subscription.unsubscribe());
        };
    }, [dispatch, endDate, groupBy, interval, pollingInterval, regions, startDate, url]);

    return {
        groupedLoadingTimes,
        isLoadingMultipleLoadingTimes,
    };
}
